import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as Papa from 'papaparse';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  ContactGroupType,
  contactGroupIncludesCall,
  contactGroupIncludesEmail,
  contactGroupIncludesWhatsapp,
} from 'src/app/utils/contact-group-type.util';
import { readImportErrors, resolvePhone, formatPhoneImportErrors, phoneImportToastOptions } from 'src/app/utils/phone-country.util';

@Component({
  selector: 'app-csv-dropzone',
  templateUrl: './csv-dropzone.component.html',
  styleUrls: ['./csv-dropzone.component.scss']
})
export class CsvDropzoneComponent implements OnInit {
  csvUploaded = false;
  columns: string[] = [];
  csvData: any[] = [];
  mappedData: any[] = [];
  form: FormGroup;
  btnLoading = false;
  
  // Validation preview properties
  showPreview = false;
  validationResults: any[] = [];
  hasValidationErrors = false;

  @Output() formSubmit: EventEmitter<any> = new EventEmitter();
  @Output() close: EventEmitter<void> = new EventEmitter();
  @Input() selectedGroupId: any;
  @Input() campaignType: ContactGroupType = 'both';
  @Input() maxContactLimit: number = 1000; // Default limit

  constructor(private fb: FormBuilder,
    private api: ContactGrouopService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
  ) {
    // Create form group - validators will be set dynamically in ngOnInit
    this.form = this.fb.group({
      username: ['', Validators.required],
      email: [''],
      phone: [''],
      countryCode: ['']
    });
  }

  ngOnInit() {
    // Set validators based on campaign type
    this.updateFormValidators();
  }

  // Update form validators based on campaign type
  private updateFormValidators() {
    const emailControl = this.form.get('email');
    const phoneControl = this.form.get('phone');
    const countryControl = this.form.get('countryCode');

    if (this.requiresEmail()) {
      emailControl?.setValidators([Validators.required]);
    } else {
      emailControl?.clearValidators();
    }

    if (this.requiresPhone()) {
      phoneControl?.setValidators([Validators.required]);
      countryControl?.setValidators([Validators.required]);
    } else {
      phoneControl?.clearValidators();
      countryControl?.clearValidators();
    }

    emailControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
    countryControl?.updateValueAndValidity();
  }

  private requiresEmail(): boolean {
    return contactGroupIncludesEmail(this.campaignType);
  }

  private requiresPhone(): boolean {
    return contactGroupIncludesWhatsapp(this.campaignType) || contactGroupIncludesCall(this.campaignType);
  }

  showEmailRequired(): boolean {
    return this.requiresEmail();
  }

  showPhoneRequired(): boolean {
    return this.requiresPhone();
  }

  // Method called when a file is selected
  onFileSelect(event: any) {
    const file = event.addedFiles[0];

    if (file) {
      this.parseCSV(file);
      this.csvUploaded = true;
    }
  }

  // Parse the CSV file
  parseCSV(file: File) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.data.length > this.maxContactLimit) {
          this.toast.error(`File contains ${result.data.length} contacts. Maximum allowed is ${this.maxContactLimit}.`, 'Upload Limit Exceeded');
          this.csvUploaded = false;
          return;
        }

        this.csvData = result.data;
        // Extract columns from the first row
        this.columns = Object.keys(this.csvData[0]);
      }
    });
  }

  // Preview data when columns are selected
  onColumnChange() {
    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;
    const selectedCountry = this.form.get('countryCode')?.value;

    // Check if required columns are selected based on campaign type
    let canPreview = selectedUsername; // Username is always required
    
    if (this.requiresEmail()) {
      canPreview = canPreview && selectedEmail;
    }
    if (this.requiresPhone() || selectedPhone) {
      canPreview = canPreview && selectedPhone && selectedCountry;
      this.form.get('countryCode')?.setValidators([Validators.required]);
    } else if (!this.requiresPhone()) {
      this.form.get('countryCode')?.clearValidators();
    }
    this.form.get('countryCode')?.updateValueAndValidity({ emitEvent: false });

    if (canPreview) {
      this.previewAndValidate();
    } else {
      this.showPreview = false;
    }
  }

  // Preview and validate the mapped data
  previewAndValidate() {
    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;
    const selectedCountry = this.form.get('countryCode')?.value;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    this.validationResults = this.csvData
      .map((row, index) => {
        const name = row[selectedUsername];
        const email = selectedEmail ? row[selectedEmail] : null;
        const phone = selectedPhone ? row[selectedPhone] : null;
        const countryCode = selectedCountry ? row[selectedCountry] : null;

        // Check if row is completely empty
        const hasName = name && name.toString().trim() !== '';
        const hasEmail = email && email.toString().trim() !== '';
        const hasPhone = phone && phone.toString().trim() !== '';
        const isEmptyRow = !hasName && !hasEmail && !hasPhone;

        // Skip completely empty rows
        if (isEmptyRow) {
          return null;
        }

        // Validate email based on campaign type
        let emailValid = true;
        let emailError = '';
        
        if (this.requiresEmail()) {
          if (!email || email.trim() === '') {
            emailValid = false;
            emailError = 'Missing (required for email campaign)';
          } else if (!emailRegex.test(email.trim())) {
            emailValid = false;
            emailError = 'Invalid format';
          }
        } else if (email && email.trim() !== '') {
          if (!emailRegex.test(email.trim())) {
            emailValid = false;
            emailError = 'Invalid format';
          }
        }

        // Validate phone based on campaign type
        let phoneValid = true;
        let phoneError = '';
        
        if (this.requiresPhone() && (!phone || phone.toString().trim() === '')) {
          phoneValid = false;
          phoneError = 'Missing (required for this campaign type)';
        } else if (phone && phone.toString().trim() !== '') {
          const resolved = resolvePhone(countryCode, phone);
          if (!resolved.ok) {
            phoneValid = false;
            phoneError = resolved.error || 'Invalid phone number';
          }
        }

        return {
          rowIndex: index + 2, // +2 because row 1 is header
          name: name || 'N/A',
          email: email || 'N/A',
          phone: phone || 'N/A',
          emailValid,
          phoneValid,
          emailError,
          phoneError,
          isValid: emailValid && phoneValid
        };
      })
      .filter(result => result !== null); // Remove null entries (empty rows)

    this.hasValidationErrors = this.validationResults.some(r => !r.isValid);
    this.showPreview = true;
  }

  // Method to map data based on user selection
  mapData() {
    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();
      return;
    }

    
    const selectedUsername = this.form.get('username')?.value;
    const selectedEmail = this.form.get('email')?.value;
    const selectedPhone = this.form.get('phone')?.value;
    const selectedCountry = this.form.get('countryCode')?.value;

    const reservedColumns = new Set(
      [selectedUsername, selectedEmail, selectedPhone, selectedCountry].filter((column) => !!column)
    );

    // Map the data
    this.mappedData = this.csvData.map((row, index) => {
      const extraFields: Record<string, string> = {};
      this.columns.forEach((column) => {
        if (reservedColumns.has(column)) {
          return;
        }
        const value = row[column];
        if (value == null || String(value).trim() === '') {
          return;
        }
        extraFields[column.trim()] = String(value).trim();
      });

      const countryCode = selectedCountry ? row[selectedCountry] : null;
      const resolved = selectedPhone && row[selectedPhone]
        ? resolvePhone(countryCode, row[selectedPhone])
        : null;

      return {
        name: row[selectedUsername],
        source: 'csv',
        email: selectedEmail ? row[selectedEmail] : null,
        phone: resolved?.ok && resolved.hasPhone ? resolved.nationalNumber : (selectedPhone ? row[selectedPhone] : null),
        country_code: resolved?.ok && resolved.hasPhone ? resolved.countryCode : (countryCode || null),
        extra_fields: Object.keys(extraFields).length ? extraFields : null,
        rowIndex: index + 2 // +2 because row 1 is header and array is 0-indexed
      };
    });

    // Filter out completely empty rows (where all fields are empty/null/undefined)
    this.mappedData = this.mappedData.filter(contact => {
      const hasName = contact.name && contact.name.toString().trim() !== '';
      const hasEmail = contact.email && contact.email.toString().trim() !== '';
      const hasPhone = contact.phone && contact.phone.toString().trim() !== '';
      return hasName || hasEmail || hasPhone; // Keep row if at least one field has data
    });

    // Check if there are any contacts after filtering
    if (this.mappedData.length === 0) {
      this.toast.error('No valid contacts found in the CSV file. Please check your file and try again.', 'Empty Contact List');
      return;
    }

    // Validate email and phone for each contact
    const validationErrors = this.validateContacts(this.mappedData);
    
    if (validationErrors.length > 0) {
      // Show validation errors
      this.showValidationErrors(validationErrors);
      return;
    }

    // Remove rowIndex before sending to API
    const cleanedData = this.mappedData.map(({ rowIndex, ...contact }) => contact);

    const payload = {
      client_id: this.localStorage.getClientId(),
      contact_group_id: this.selectedGroupId,
      contacts: cleanedData
    };
    
    this.btnLoading = true;
    this.api.createContactGroupList(payload).subscribe(
      (response:any) => {
        this.toast.success('Contacts uploaded successfully!');
        this.formSubmit.emit();
      },
      (error:any) => {
        console.log(error);
        const apiErrors = readImportErrors(error);
        this.toast.error(
          apiErrors.length ? formatPhoneImportErrors(apiErrors) : 'Error while creating contact group',
          'Import validation',
          phoneImportToastOptions
        );
        this.btnLoading = false;
      }
    );
  }

  // Validate contacts for email and phone format
  private validateContacts(contacts: any[]): string[] {
    const errors: string[] = [];
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    contacts.forEach((contact) => {
      const { email, phone, country_code, name, rowIndex } = contact;

      // Validate email based on campaign type
      if (this.requiresEmail()) {
        if (!email || email.trim() === '') {
          errors.push(`Row ${rowIndex}: Email is missing for contact "${name || 'Unknown'}" (required for email campaign)`);
        } else if (!emailRegex.test(email.trim())) {
          errors.push(`Row ${rowIndex}: Invalid email format "${email}" for contact "${name || 'Unknown'}"`);
        }
      } else if (email && email.trim() !== '') {
        if (!emailRegex.test(email.trim())) {
          errors.push(`Row ${rowIndex}: Invalid email format "${email}" for contact "${name || 'Unknown'}"`);
        }
      }

      if (this.requiresPhone() && (!phone || phone.toString().trim() === '')) {
        errors.push(`Row ${rowIndex}: Phone number is missing for contact "${name || 'Unknown'}" (required for this campaign type)`);
      } else if (phone && phone.toString().trim() !== '') {
        const resolved = resolvePhone(country_code, phone);
        if (!resolved.ok) {
          errors.push(`Row ${rowIndex}: ${resolved.error} for contact "${name || 'Unknown'}"`);
        }
      }
    });

    return errors;
  }

  // Show validation errors to user
  private showValidationErrors(errors: string[]) {
    const shown = errors.slice(0, 8).map((line) =>
      line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    );
    const remaining = errors.length - shown.length;
    const message = shown.join('<br>')
      + (remaining > 0 ? `<br><br>and ${remaining} more. Fix these rows in the file and upload again.` : '');
    this.toast.error(message, 'Validation Failed', phoneImportToastOptions);
  }

  // Helper method to check if a form control is invalid and touched
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field!.invalid && field!.touched;
  }

  // Get count of valid rows
  getValidCount(): number {
    return this.validationResults.filter(r => r.isValid).length;
  }

  // Get count of invalid rows
  getInvalidCount(): number {
    return this.validationResults.filter(r => !r.isValid).length;
  }

  // Remove all invalid rows from the data
  removeInvalidRows() {
    const invalidCount = this.getInvalidCount();
    
    // Filter out invalid rows from csvData
    const validIndices = this.validationResults
      .filter(r => r.isValid)
      .map(r => r.rowIndex - 2); // Convert back to 0-based index

    this.csvData = this.csvData.filter((_, index) => validIndices.includes(index));
    
    // Re-run validation
    this.previewAndValidate();
    
    this.toast.success(`Removed ${invalidCount} invalid row(s)`, 'Cleanup Complete');
  }

  // Scroll to first error in the table
  scrollToFirstError() {
    const firstInvalidIndex = this.validationResults.findIndex(r => !r.isValid);
    
    if (firstInvalidIndex !== -1) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const tableBody = document.querySelector('.preview-table tbody');
        const errorRow = tableBody?.querySelectorAll('tr')[firstInvalidIndex];
        
        if (errorRow) {
          errorRow.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Add temporary highlight effect
          errorRow.classList.add('highlight-error');
          setTimeout(() => {
            errorRow.classList.remove('highlight-error');
          }, 2000);
        }
      }, 100);
    }
  }

  // Emit close event
  onClose() {
    this.close.emit();
  }
}
